/**
 * "What has this customer already been sent?" (#1545)
 *
 * One shared read behind every surface that asks: the job header's Resend
 * button, the Paperwork card's packet strip, the NOW panel's "N never got the
 * packet" row, and the Comms tab's per-send rows. They all mount on the same
 * page at the same time, so they share one query key and React Query collapses
 * them into a single request.
 *
 * `everSent` is the gate the whole feature hangs on, and it is the SERVER's
 * answer, not a guess from job status: the first send is the one that flips the
 * job, issues the draft invoice and can charge a card on file, so it stays in
 * close-out behind those gates. A resend can do none of that — which is what
 * makes a one-click button on the job page safe.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

/** sent = handed to SendGrid, no word back yet · delivered = their server took it
 *  · undelivered = bounced / blocked / dropped after SendGrid accepted it
 *  · failed = refused at send time · dropped = this person was sent nothing. */
export type SendHistoryStatus = "sent" | "delivered" | "undelivered" | "failed" | "dropped";

export interface SendHistoryRecipient {
    /** Ledger row id — the handle "View the email" opens. Null for someone who
     *  received no email at all, who has nothing to show. */
    ledgerId: string | null;
    email: string;
    contactName: string | null;
    documents: string[];
    status: SendHistoryStatus;
    /** The provider's error, why it didn't arrive, or why a dropped recipient got nothing. */
    reason: string | null;
    /** When they first opened it, if their mail app reported it. */
    openedAt?: string | null;
    /** False for every send made before Groundwork started keeping the message
     *  (migration 611). The UI must then offer no link rather than re-render
     *  today's template as if it were what the customer received. */
    hasBody: boolean;
    /** Deep-link into Mailroom, when the office mailbox holds the copy. */
    mailroom: { threadId: string; accountId: string } | null;
}

export interface SendHistoryEntry {
    id: string;
    sentAt: string;
    sentByName: string | null;
    subject: string | null;
    recipients: SendHistoryRecipient[];
    delivered: number;
    failed: number;
    dropped: number;
    /** Accepted, then bounced or blocked by their mail server. */
    undelivered?: number;
    /** 'email', or how the office delivered it without email (2026-10-07):
     *  portal | mail | in_person | other. Older servers omit it (= email). */
    channel?: string;
    /** "FWISD vendor portal". */
    channelLabel?: string | null;
    note?: string | null;
    /** Proof file for a delivery made another way. */
    proofAttachmentId?: string | null;
    /** A "Send to staff only" TEST copy: never a delivery to the customer. */
    internalCopy?: boolean;
}

/** A delivery the office recorded without email (portal, mail, in person). */
export function isOtherWaySend(s: Pick<SendHistoryEntry, "channel">): boolean {
    return !!s.channel && s.channel !== "email";
}

export interface SendHistory {
    sends: SendHistoryEntry[];
    /** True once at least one packet email has actually been delivered. */
    everSent: boolean;
    lastSentAt: string | null;
    /** Failed + received-nothing on the MOST RECENT send. */
    missedOnLastSend: number;
}

export function useSendHistory(jobId: string | undefined, enabled = true) {
    return useQuery<SendHistory>({
        queryKey: ["send-history", jobId],
        queryFn: () => api.get<SendHistory>(`/api/office-review/jobs/${jobId}/send-history`),
        enabled: !!jobId && enabled,
        staleTime: 30_000,
    });
}

/** "/mailroom?thread=…&account=…" — the URL the Mailroom page consumes. */
export function mailroomThreadUrl(m: { threadId: string; accountId: string }): string {
    return `/mailroom?thread=${encodeURIComponent(m.threadId)}&account=${encodeURIComponent(m.accountId)}`;
}
